import React from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';

/**
 * Lightweight modal shown when the user taps the floating Info
 * button on the review surface. Surfaces the scan's scene-level
 * metadata — `room_context` as an uppercase pill, `ai_description`
 * as a paragraph.
 *
 * Handles the still-scanning state gracefully: both fields arrive
 * near the end of the SSE stream, so an early tap shows a waiting
 * placeholder instead of a blank modal.
 *
 * Reuses the same backdrop + sheet shell as {@link NameDraftItemSheet}
 * so the aesthetic is consistent across the stack.
 */

interface ImageInfoSheetProps {
  visible: boolean;
  roomContext?: string | null;
  aiDescription?: string | null;
  /** True while the scan is still running and these fields may
   *  not have arrived yet. */
  stillScanning?: boolean;
  onClose: () => void;
}

export const ImageInfoSheet: React.FC<ImageInfoSheetProps> = ({
  visible,
  roomContext,
  aiDescription,
  stillScanning,
  onClose,
}) => {
  const hasContent =
    (typeof roomContext === 'string' && roomContext.length > 0) ||
    (typeof aiDescription === 'string' && aiDescription.length > 0);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        onPress={onClose}
        style={styles.backdrop}
        accessibilityRole="button"
        accessibilityLabel="Dismiss image info"
      >
        {/* Inner Pressable stops backdrop-tap from closing when the
            user taps inside the sheet itself. */}
        <Pressable onPress={() => {}} style={styles.sheet}>
          <View style={styles.header}>
            <View style={styles.headerIcon}>
              <MaterialIcons
                name="auto-awesome"
                size={18}
                color={fpColors.primary}
              />
            </View>
            <Text style={styles.title}>About this scan</Text>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              style={styles.closeBtn}
              accessibilityLabel="Close"
            >
              <MaterialIcons
                name="close"
                size={18}
                color={fpColors.onSurfaceVariant}
              />
            </Pressable>
          </View>

          {roomContext ? (
            <View style={styles.roomPill}>
              <MaterialIcons
                name="place"
                size={12}
                color={fpColors.primary}
              />
              <Text style={styles.roomPillText}>{roomContext}</Text>
            </View>
          ) : null}

          {aiDescription ? (
            <Text style={styles.body}>{aiDescription}</Text>
          ) : stillScanning ? (
            <View style={styles.waitBlock}>
              <MaterialIcons
                name="hourglass-empty"
                size={14}
                color={fpColors.onSurfaceVariant}
              />
              <Text style={styles.waitText}>
                Still scanning\u2026 description appears once Vision
                finishes.
              </Text>
            </View>
          ) : !hasContent ? (
            <Text style={styles.emptyText}>
              No scene metadata available for this image.
            </Text>
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    padding: 16,
  },
  sheet: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 16,
    padding: 18,
    maxWidth: 460,
    alignSelf: 'center',
    width: '100%',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 20px 48px rgba(31,26,34,0.25)' } as any)
      : null),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  headerIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}14`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  roomPill: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: `${fpColors.primary}14`,
    borderWidth: 1,
    borderColor: `${fpColors.primary}40`,
    marginBottom: 10,
  },
  roomPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: fpColors.primary,
  },
  body: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurface,
  },
  waitBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    padding: 10,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  waitText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurfaceVariant,
  },
  emptyText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
});
